<script setup lang="ts">
import { buildBackgroundMenu } from '@/components/document/sub-components/file-manager/logic/core/contextMenuBuilders';
import { openContextMenuIfAny, useFileManagerContextMenu } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerContextMenu';
import { useFileManagerCreateFolder } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerCreateFolder';
import { useFileManagerTreeRow } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerTreeRow';
import { useFileManagerActionVisibility } from '@/components/document/sub-components/file-manager/permissions/useFileManagerActionVisibility';
import { Spinner } from '@/components/document/sub-components/file-manager/sub-components/spinner';
import { computed } from 'vue';
import { DirectoryList } from './sub-components/directory-list';

// The virtual root has no row of its own any more. It used to be a "project root" row naming the
// project, and this component now knows nothing about projects — the pane can show several at once, and
// on the project page CESGProjectMenu already prints the project name directly above the pane, so no
// information is lost. What is left is the root's *listing*, rendered as the tree's top level.
//
// `() => '/'` rather than the literal '/': useFileManagerTreeRow always forwards a getter to
// useDirectoryChildrenQuery, and a raw literal here is the exact call-site shape of the TanStack
// queryFn-hijack bug that composable's doc comment documents. Eager (no knownHasFolderChildren) —
// there is no parent listing to draw a guess from.
const { isLoading, directories } = useFileManagerTreeRow(() => '/');

// Background-only: a folder row (.directory-item) already has its own dedicated menu
// (useDirectoryTreeContextMenu) — this one is for right-clicking anywhere else in the tree. Unlike the
// grid's own background "Create folder" (which targets currentPath), this always targets the root. No
// "Create file" entry either — the tree only ever lists directories, so uploading a file here would
// have nothing visible to update; that's an intentional, permanent difference from the grid's
// background menu, not a gap to close.
//
// Usually the root is a listing of the mount table, with no drive of its own to create anything in,
// so canCreateFolderAt('/') is false and this menu has no entries at all — the tree's background
// right-click then falls through to the native browser menu, and creating a folder from the tree
// means right-clicking a library or folder row, which targets that row's own path.
//
// It IS live when the host grafts its only mount at the root (a project exposing one library): the
// root is then a writable drive folder, and this creates there. Headerless either way — the root has
// no name of its own to show, which is also why the header this menu used to carry left with the
// project prop. The handler is kept rather than deleted because a per-library or per-project entry
// may well come back here.
const { showCreateFolderAt } = useFileManagerActionVisibility();
const contextMenu = useFileManagerContextMenu();
const createFolderState = useFileManagerCreateFolder();

const backgroundMenuItems = computed(() =>
  buildBackgroundMenu({
    createFolder:
      showCreateFolderAt('/') && createFolderState.canCreateFolderAt('/')
        ? { request: () => createFolderState.openModal('/') }
        : undefined,
  }),
);

const onBackgroundContextMenu = (event: MouseEvent) => {
  if ((event.target as HTMLElement).closest('.directory-item')) return;
  const opened = openContextMenuIfAny(
    { x: event.clientX, y: event.clientY },
    backgroundMenuItems.value,
    contextMenu,
  );
  if (opened) event.preventDefault();
};
</script>

<template>
  <div class="directory-list-view" @contextmenu="onBackgroundContextMenu">
    <Spinner v-if="isLoading" />
    <DirectoryList v-else :items="directories" />
  </div>
</template>

<style scoped>
.directory-list-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  /* Breathing room above and to the right of the rows so a hover/selected highlight — a rounded
     rectangle spanning the full row — reads as a floating pill rather than butting against the pane's
     edge. Placed here rather than on .directory-list, which recurses for every expanded subtree and
     would compound the inset at each nested level. The left side already gets its inset from
     .directory-list's padding-left, which is deliberately per-level: that is what indents the tree. */
  padding-top: 0.5rem;
  padding-right: 0.5rem;
  overflow-y: auto;
}
</style>
